import React, { useEffect, useState } from 'react';
import { renderLinkedQuestion,  getMessages,  getLinkedQuestions, QuestionGroupViewPropTypes } from '../../utils';
import * as CB from '../../../neuro-ui/lib/CardBuilder';
import NeuroMessage from '../NeuroMessage';
import {
  builderAnswerChanged,
  builderGroupValidation,
  builderQuestionValidation,
  PageAnimationStages,
  showValidationErrors,
  useDispatch
} from '../../state';
import { useNeuroMessages } from '../MessageContext';
import {useAnswers, usePages} from '../../hooks';
import Tooltip from '../Tooltip';

const CardBuilder = ({ questionGroup, renderQuestion, page, customProps }) => {
  const messageContext = useNeuroMessages();
  const { answers, parseAnswerValue } = useAnswers();
  const { validationErrors = [], isAnimatingPages } = usePages();
  const dispatch = useDispatch();
  const builderKey = questionGroup.attributes.questionGroupKey;
  const builderOptions = questionGroup.questions.find(
    (q) => q.appkey === builderKey
  );
  const value = answers[builderOptions.appkey];
  const { value: currentValues = [] } = value || {};
  const [activeCard, setActiveCard] = useState(false);
  const [disabledBuilder, setDisabledBuilder] = useState(false);
  const builderOptionsToggleNoneKey = (builderOptions.attributes || {}).toggleNone || false;

  useEffect(() => {
    if(isAnimatingPages === PageAnimationStages.COMPLETED) {
      validateBuilder(currentValues);
    }
  }, [isAnimatingPages]);

  useEffect(() => {
    setDisabledBuilder(
      (currentValues || []).includes(builderOptionsToggleNoneKey)
    );
  }, [value]);

  const builderLinkedQuestions = (builderOptions.answers || []).map(
    (option) => {
      const optionTitleMessageKey = `${builderOptions.appkey}.${option.key}`;
      const optionCardTitleMessageKey = `${optionTitleMessageKey}.cardLabel`;
      return {
        id: option.key,
        labelMessageId: messageContext.hasMessage(optionCardTitleMessageKey)
          ? optionCardTitleMessageKey
          : optionTitleMessageKey,
        questions: getLinkedQuestions(
          questionGroup,
          option.attributes.linkedQuestions || []
        ),
      };
    }
  );

  const getBuilderMessages = () => {
    const getCustomMessage = (key) => messageContext.getMessage(`${builderKey}.${key}`);
    const customMessages = {
      'cardBuilder.addCard': getCustomMessage('cardBuilder.addCard'),
      'cardBuilder.cancelEdit': getCustomMessage('cardBuilder.cancelEdit'),
      'cardBuilder.editCard': getCustomMessage('cardBuilder.editCard'),
      'cardBuilder.removeCard': getCustomMessage('cardBuilder.removeCard'),
      'cardBuilder.saveCard': getCustomMessage('cardBuilder.saveCard'),
      'cardBuilder.modal.title': getCustomMessage('cardBuilder.modal.title'),
    };
    return {
      ...getMessages(messageContext.locale),
      ...customMessages,
    };
  };

  const renderCardBodyFn = ({ card, isActive }) => {
    let render = null;
    if (isActive) {
      render = card.questions.map((q) => {
        const answerValue = Object.prototype.hasOwnProperty.call(page.answers, q.appkey)
          ? parseAnswerValue({ answer: page.answers[q.appkey] })
          : null;
        const messageKey = `${q.appkey}.cardBuilder.answerLabel`;
        return answerValue ? (
          <div className={`${CB.cssBEM}__answer`} key={q.appkey}>
            <span className={`${CB.cssBEM}__answer-label`}>
              <NeuroMessage id={messageContext.hasMessage(messageKey) ? messageKey : q.appkey} />
              <NeuroMessage id={`${messageKey}.tooltip`} shouldHideIfNotFound={true}>
                {() => <Tooltip className={`${CB.cssBEM}__answer-tooltip`} messageId={`${messageKey}.tooltip`}/>}
              </NeuroMessage>
            </span>
            <span className={`${CB.cssBEM}__answer-value`}>{answerValue}</span>
          </div>
        ) : null;
      });
    }
    return render;
  };

  const renderModalBodyFn = (card) =>
    messageContext.hasMessage(`${card.id}.cardBuilder.body`) && (
      <NeuroMessage id={`${card.id}.cardBuilder.body`} />
    );

  const renderBuilderOptionsQuestion = () => {
    const onCardOptionsValueChange = ({ value, saveChangedValue }) => {
      const optionHasBeenAdded = Array.isArray(value)
        ? value.length > currentValues.length
        : value;
      const toggleNoneHasBeenAdded =
        builderOptionsToggleNoneKey &&
        Array.isArray(value) &&
        value.includes(builderOptionsToggleNoneKey);
      if (toggleNoneHasBeenAdded) {
        currentValues.map((v) => onRemove({ appkey: v, currentValues }));
        setActiveCard(false);
      } else if (optionHasBeenAdded) {
        const selectedOptionAppKey = Array.isArray(value)
          ? value[value.length - 1]
          : value;
        const doesCardExist = builderLinkedQuestions.find(
          ({ id }) => id === selectedOptionAppKey
        );
        if (doesCardExist) {
          setActiveCard(selectedOptionAppKey);
        }
      }
      validateBuilder(value);
      saveChangedValue(value);
    };

    return renderQuestion({
      ...builderOptions,
      attributes: {
        ...(builderOptions.attributes || {}),
        valueChanged: onCardOptionsValueChange,
      },
    });
  };

  const hasErrorsInCard = (card) => {
    const validationErrorQuestionAppKeys = validationErrors
      .filter((v) => v?.fieldType === 'question')
      .map((v) => v?.key);
    const cardQuestionsWithErrors = (card?.questions || []).filter((q) =>
      validationErrorQuestionAppKeys.includes(q?.appkey)
    );
    return cardQuestionsWithErrors.length > 0;
  };

  const onAdd = ({ appkey, currentValues }) => {
    const value = [...currentValues.filter((v) => v !== appkey), appkey];
    dispatch(
      builderAnswerChanged({ question: builderOptions, questionGroup, value })
    );
  };
  const onRemove = ({ appkey, currentValues }) => {
    const value = currentValues.filter((v) => v !== appkey);
    dispatch(
      builderAnswerChanged({
        question: builderOptions,
        questionGroup,
        value,
        hasBeenRemoved: appkey,
        page
      })
    );
  };
  const linkedQuestionValueChanged = (linkedQuestion, value) => {
    dispatch(
      builderQuestionValidation({
        linkedQuestion,
        questionGroup,
        shouldDisplay: true,
        answerValue: value
      })
    );
  };

  const validateBuilder = (value) => {
    dispatch( builderGroupValidation({ questionGroup, question: builderOptions, value }) );
  };

  const onDisabledClick = () => {
    dispatch(showValidationErrors());
  };

  const onChange = ({ card, action }) => {
    switch (action) {
      case CB.cardBuilderActions.CARD_SAVED: {
        onDisabledClick();
        if (!hasErrorsInCard(card)) {
          setActiveCard(false);
        }
        break;
      }
      case CB.cardBuilderActions.MODAL_CLOSED: {
        setActiveCard(false);
        break;
      }
      case CB.cardBuilderActions.CARD_ADDED: {
        setActiveCard(card.id);
        onAdd({
          appkey: card.id,
          currentValues,
        });
        break;
      }
      case CB.cardBuilderActions.CARD_REMOVED: {
        setActiveCard(false);
        onRemove({
          appkey: card.id,
          currentValues,
        });
        break;
      }
      case CB.cardBuilderActions.EDIT: {
        setActiveCard(card.id);
        break;
      }
      default:
        break;
    }
  };

  return (
    <div>
      <CB.CardBuilderNUI
        {...customProps}
        builderKey={builderKey}
        cards={builderLinkedQuestions}
        disabled={disabledBuilder}
        locale={messageContext.locale}
        MessageComponent={NeuroMessage}
        messages={getBuilderMessages()}
        nodeId={questionGroup.attributes.modalNodeId || 'root'}
        onChange={onChange}
        values={currentValues}
        activeCardId={activeCard}
        renderQuestion={(linkedQuestion) =>
          renderLinkedQuestion(
            linkedQuestion,
            linkedQuestionValueChanged,
            renderQuestion
          )
        }
        renderOptions={renderBuilderOptionsQuestion}
        renderCardBodyFn={renderCardBodyFn}
        renderModalBodyFn={renderModalBodyFn}
        showAllMode={questionGroup?.attributes?.showAllMode || false}
      />
    </div>
  );
};

CardBuilder.propTypes = QuestionGroupViewPropTypes;

export default CardBuilder;
